import {
  KalturaRecording,
  RecordingAddAction,
  KalturaFilterPager,
  RecordingListAction,
  RecordingGetAction,
  KalturaRecordingArgs,
  KalturaRecordingType,
  KalturaHouseholdQuota,
  RecordingDeleteAction,
  RecordingCancelAction,
  KalturaRecordingStatus,
  KalturaRecordingFilter,
  KalturaSeriesRecording,
  RecordingListActionArgs,
  HouseholdQuotaGetAction,
  SeriesRecordingAddAction,
  SeriesRecordingListAction,
  RecordingCancelActionArgs,
  RecordingDeleteActionArgs,
  KalturaSeriesRecordingArgs,
  SeriesRecordingCancelAction,
  KalturaRecordingListResponse,
  KalturaSeriesRecordingFilter,
  SeriesRecordingCancelActionArgs,
  KalturaSeriesRecordingListResponse,
  SeriesRecordingCancelByEpgIdAction,
  SeriesRecordingCancelByEpgIdActionArgs,
  KalturaRecordingOrderBy,
  KalturaAssetHistoryFilter,
  KalturaAsset,
  KalturaGroupingOption,
  KalturaAssetType,
} from '@kaltura-ott/ott-client-types/api/types';
import { Observable, of, forkJoin, throwError } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';

import { KuxProgramType } from '../../interfaces/asset';
import { KuxSeriesRecord, Record, KuxRecord } from '../../interfaces/record';
import { getAssetHistoryListRecursively, getScheduledRecordingAssetsList, getAssetRecordingList } from '../asset';
import { assetToRecord, assetsToType, assetToProgram } from '../asset-v2.helper';
import { getPageNumberArr } from '../asset.helper';
import { MediaTypeId } from '../dms';
import { kalturaClient } from '../kaltura-clent';

import { normalizingRecord, normalizingSeriesRecord } from './records.helper';

export interface KuxRecordingsListArgs {
  orderBy?: string;
  statusIn?: string;
  pageSize?: number;
  pageIndex?: number;
  groupingOptions?: KalturaGroupingOption;
}

/**
 * Get subscriber available space details
 */
export const getHouseholdQuota = (): Observable<KalturaHouseholdQuota> => {
  const action = new HouseholdQuotaGetAction();
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return throwError(error);
    }),
  );
};

/**
 * Get single recording list
 */
const getRecordingList = (recordingListActionArgs: RecordingListActionArgs) => {
  const action = new RecordingListAction(recordingListActionArgs);
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return of(error);
    }),
  );
};

interface GetRecordsRecursivelyArgs {
  filter: KalturaRecordingFilter;
  pageSize: number;
}

export const getRecordingsRecursively = <T extends KalturaRecordingListResponse>({
  filter,
  pageSize,
}: GetRecordsRecursivelyArgs): Observable<T> => {
  const action = new RecordingListAction({
    filter: new KalturaRecordingFilter(filter),
    pager: new KalturaFilterPager({
      pageIndex: 1,
      pageSize,
    }),
  });

  const client = kalturaClient();
  return client.request(action).pipe(
    switchMap((assets) => {
      const paginatorRequestArr = getPageNumberArr(assets.totalCount, pageSize, 2).map((pageNumber) =>
        getRecordingList({
          filter,
          pager: new KalturaFilterPager({
            pageIndex: pageNumber,
            pageSize,
          }),
        }),
      );
      return forkJoin([of(assets), ...paginatorRequestArr]);
    }),
    map((result) => {
      return {
        ...result[0],
        totalCount: result[0].totalCount,
        objects: result.reduce((prev, cur) => [...prev, ...cur.objects], []),
      };
    }),
  ) as Observable<T>;
};

/**
 * Get series recording list
 */
export const getSeriesRecordingList = (): Observable<KalturaSeriesRecordingListResponse> => {
  const action = new SeriesRecordingListAction({
    filter: new KalturaSeriesRecordingFilter({}),
  });

  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return throwError(error);
    }),
  );
};

/**
 * Add single recording
 */
export const addRecording = (newRecordingArgs: KalturaRecordingArgs): Observable<KalturaRecording> => {
  const action = new RecordingAddAction({
    recording: new KalturaRecording({
      ...newRecordingArgs,
    }),
  });

  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return throwError(error);
    }),
  );
};

/**
 * Add single recording
 */
export const addRecordingV2 = (newRecordingArgs: KalturaRecordingArgs): Observable<KuxRecord | undefined> => {
  const action = new RecordingAddAction({
    recording: new KalturaRecording({
      ...newRecordingArgs,
    }),
  });

  const client = kalturaClient();
  return client.request(action).pipe(
    map((record) => assetToRecord(record)),
    catchError((error) => {
      return throwError(error);
    }),
  );
};

/**
 * Add series recordingv2
 */
export const addSeriesRecordingV2 = (
  newSeriesRecordingArgs: KalturaSeriesRecordingArgs,
  pageSize: number,
): Observable<{ series: KalturaSeriesRecording; singles: (KuxRecord | undefined)[] }> => {
  const action = new SeriesRecordingAddAction({
    recording: new KalturaSeriesRecording({
      ...newSeriesRecordingArgs,
    }),
  });

  const client = kalturaClient();
  return client.request(action).pipe(
    switchMap((series) => {
      return getRecordingsRecursively({
        filter: new KalturaRecordingFilter({
          kSql: `(and SeriesId='${series.seriesId}')`,
          statusIn: KalturaRecordingStatus.recorded,
        }),
        pageSize,
      }).pipe(
        map((records) => {
          return {
            series,
            singles: records?.objects?.map((record) => assetToRecord(record)),
          };
        }),
      );
    }),
    catchError((error) => {
      return throwError(error);
    }),
  );
};

/**
 * Cancel program as single
 */
export const cancelRecording = ({ id }: RecordingCancelActionArgs): Observable<KalturaRecording> => {
  const action = new RecordingCancelAction({ id });

  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return throwError(error);
    }),
  );
};

/**
 * Cancel program as series
 */
export const cancelSeriesRecording = ({ id }: SeriesRecordingCancelActionArgs): Observable<KalturaSeriesRecording> => {
  const action = new SeriesRecordingCancelAction({ id });
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return throwError(error);
    }),
  );
};

/**
 * Cancel program as series by epg id
 */
export const cancelSeriesRecordingByEpgId = (
  cancelSeriesByEpgArgs: SeriesRecordingCancelByEpgIdActionArgs,
): Observable<KalturaSeriesRecording> => {
  const action = new SeriesRecordingCancelByEpgIdAction(cancelSeriesByEpgArgs);
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return throwError(error);
    }),
  );
};

/**
 * Delete program as single
 */
export const deleteRecording = ({ id }: RecordingDeleteActionArgs): Observable<KalturaRecording> => {
  const action = new RecordingDeleteAction({ id });
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return throwError(error);
    }),
  );
};

/**
 * Get assets for single recordings
 */
export const getSingleRecordsAssetsV2 = ({
  kSql = '',
  groupBy = '',
  orderBy = KalturaRecordingOrderBy.startDateDesc,
  pageSize,
  pageIndex,
  groupingOptions,
}: KuxRecordingsListArgs & { kSql?: string; groupBy?: string }): Observable<{
  items: KalturaAsset[];
  totalCount: number;
}> => {
  return getAssetRecordingList(kSql, groupBy, pageIndex, pageSize, orderBy, groupingOptions).pipe(
    map((assets) => {
      return {
        items: assets.objects || [],
        totalCount: assets.totalCount || 0,
      };
    }),
  );
};

/**
 * Get assets for single recordings
 */
export const getScheduledRecordingAssets = <T extends KuxProgramType & { record: KuxRecord }>(
  pageIndex?: number,
  pageSize?: number,
  seriesId?: string,
  groupBy?: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Observable<any> => {
  return getScheduledRecordingAssetsList(pageIndex, pageSize, seriesId, groupBy).pipe(
    map((assets) => {
      return {
        items: assetsToType<KalturaAsset, T>(assets.objects),
        totalCount: assets.totalCount,
      };
    }),
  );
};

/**
 *  Get record's history\bookmark
 *  @param recordId Number
 *  */
export const getRecordHistory = (recordId: number) => {
  return getAssetHistoryListRecursively({
    filter: new KalturaAssetHistoryFilter({
      assetIdIn: recordId.toString(),
      typeIn: MediaTypeId.Record,
    }),
  }).pipe(
    map((history) => {
      if (!history?.objects?.length) return;
      return history.objects[0];
    }),
    catchError((error) => {
      return throwError(error);
    }),
  );
};

/**
 * Get all records list
 */

export const getSingleAndSeriesRecordListV2 = (
  filter: KuxRecordingsListArgs,
  pageSize: number,
): Observable<{ series: KuxSeriesRecord[]; singles: Record[] }> => {
  return forkJoin({
    series: getSeriesRecordingList(),
    singles: getRecordingsRecursively({ filter: new KalturaRecordingFilter(filter), pageSize }),
  }).pipe(
    map(
      ({ singles, series }) => ({
        series: series?.objects?.map(normalizingSeriesRecord),
        singles: singles?.objects?.map(normalizingRecord),
      }),
      catchError(() => {
        return of({});
      }),
    ),
  );
};

/**
 * Delete records list
 */
export const deleteRecordsList = (records: KuxRecord[]) => {
  return forkJoin(
    records.map((record) => {
      if (record.status === KalturaRecordingStatus.recorded) {
        return deleteRecording({ id: record.id });
      }
      if (record.type === KalturaRecordingType.series) {
        return cancelSeriesRecording({ id: record.id });
      }
    }),
  );
};

export const getRecordingById = (id: number) => {
  const action = new RecordingGetAction({ id });
  const client = kalturaClient();
  return client.request(action).pipe(
    map((record) => normalizingRecord(record)),
    catchError((error) => {
      return throwError(error);
    }),
  );
};

export const getRecordingProgram = (
  ids: (string | undefined)[],
  assetType: KalturaAssetType,
  isDetailsSeries: boolean | undefined,
  isNewNpvr = false,
): Observable<KuxProgramType> => {
  const seriesIDsKsql = `(and ${ids.map((id) => `SeriesID='${id}'`).join(' ')})`;
  const singleKsql = `(and ${ids.map((id) => `${assetType}_id='${id}'`).join(' ')})`;
  const ksql = isDetailsSeries ? seriesIDsKsql : singleKsql;
  return getAssetRecordingList(
    ksql,
    isNewNpvr ? 'SeriesID' : undefined,
    1,
    1,
    undefined,
    isNewNpvr ? KalturaGroupingOption.include : undefined,
  ).pipe(
    map((assets) => assets.objects[0] && assetToProgram(assets.objects[0])),
    first(),
  );
};
